import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Select, Typography, Space, message, Tag, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';

const { Title } = Typography;

const MEETING_TYPES = [
  { value: "operative", label: "Оперативное совещание по проекту (оперативка)" },
  { value: "gencontractor", label: "Планёрка генподрядчика" },
  { value: "schedule", label: "Совещание по срокам и графику (schedule meeting)" },
  { value: "budget", label: "Совещание по бюджету и затратам" },
  { value: "procurement", label: "Совещание по закупкам и тендерам" },
  { value: "design", label: "Совещание по проектированию" },
  { value: "sales", label: "Совещание по продажам и маркетингу" },
  { value: "development", label: "Совещание по земельному банку и девелопменту" },
  { value: "approuals", label: "Совещание по согласованиям и разрешениям" },
  { value: "quality", label: "Совещание по качеству и техническому надзору" },
  { value: "safety", label: "Совещание по охране труда и промышленной безопасности (ОТ и ПБ)" },
  { value: "commissioning", label: "Совещание по вводу в эксплуатацию и передаче ключей" },
  { value: "legal", label: "Совещание по юридическим вопросам и претензионной работе" },
  { value: "strategic", label: "Стратегическая сессия / совещание совета директоров" },
  { value: "digital", label: "Совещание по цифровизации и BIM" }
];

export default function RegularMeetingsPage() {
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role_level >= 5 || user?.username === 'khaminov');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [meetingsRes, usersRes] = await Promise.all([
        api.get('/regular-meetings'),
        api.get('/auth/users')
      ]);
      setMeetings(meetingsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      message.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (values) => {
    try {
      await api.post('/regular-meetings', values);
      message.success('Регулярное совещание создано');
      setIsModalOpen(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error('Ошибка создания');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/regular-meetings/${id}`);
      message.success('Регулярное совещание удалено');
      fetchData();
    } catch (error) {
      message.error('Ошибка при удалении');
    }
  };

  const columns = [
    {
      title: 'Тип (Название)',
      dataIndex: 'meeting_type',
      key: 'meeting_type',
      render: (text) => {
        const type = MEETING_TYPES.find(t => t.value === text);
        return <Tag color="purple">{type ? type.label : text}</Tag>;
      }
    },
    {
      title: 'Секретарь',
      dataIndex: 'secretary_id',
      key: 'secretary_id',
      render: (id) => users.find(u => String(u.id) === String(id))?.full_name || id
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button onClick={() => navigate(`/regular-meetings/${record.id}`)}>
            Открыть
          </Button>
          {isAdmin && (
            <Popconfirm
              title="Удалить это регулярное совещание?"
              onConfirm={() => handleDelete(record.id)}
              okText="Да"
              cancelText="Нет"
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Регулярные совещания</h1>
          <p className="page-subtitle">Плановые встречи и совещания</p>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => setIsModalOpen(true)}
          style={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none',
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
          }}
        >
          Создать
        </Button>
      </div>

      <Card style={{ borderRadius: 16 }} styles={{ body: { padding: 0 } }}>
        <Table 
          columns={columns} 
          dataSource={meetings} 
          rowKey="id" 
          loading={loading}
          size="small"
          className="super-compact-table"
        />
      </Card>

      <Modal
        title="Создать регулярное совещание"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="meeting_type" label="Тип совещания" rules={[{ required: true }]}>
            <Select options={MEETING_TYPES} />
          </Form.Item>
          <Form.Item name="secretary_id" label="Секретарь" rules={[{ required: true }]}>
            <Select
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>
          <Form.Item name="participant_ids" label="Участники" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
